import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, Smartphone, Copy, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { formatPrice } from "../lib/format";

export default function PayListing() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ listing_fee: 2000, momo_number: "", momo_name: "" });
  const [listing, setListing] = useState(null);
  const [payPhone, setPayPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paid, setPaid] = useState(false);
  const [payCode, setPayCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  const confirmPayment = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post(`/listings/${id}/pay`, { phone: payPhone, reference: id });
      setPayCode(res.data.confirmation_code || "");
      setPaid(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 pt-24 pb-16">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6" data-testid="pay-back-btn">
        <ArrowLeft size={16} /> {t("nav_dashboard")}
      </button>

      {error && <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 text-sm">{error}</div>}

      {!paid ? (
        <div className="bg-[#121212] border border-white/10 p-8" data-testid="pay-listing-page">
          <div className="flex items-center gap-3 mb-2">
            <Smartphone className="text-gold" />
            <h2 className="font-display text-2xl text-white">{t("pay_title")}</h2>
          </div>
          {listing && <p className="text-white/50 text-sm mb-6">{listing.title}</p>}

          <div className="bg-black/40 border border-white/10 p-5 space-y-4">
            <Row label={lang === "en" ? "Listing fee" : "Frais d'annonce"} value={formatPrice(config.listing_fee)} highlight />
            <Row label={lang === "en" ? "Pay to (MTN MoMo)" : "Payer à (MTN MoMo)"} value={config.momo_number} copy />
            {config.momo_name ? <Row label={lang === "en" ? "Account name" : "Nom du compte"} value={config.momo_name} /> : null}
            <Row label={lang === "en" ? "Reference" : "Référence"} value={id} copy />
          </div>

          <div className="mt-6 text-sm text-white/60 leading-relaxed bg-gold/5 border border-gold/20 p-4">
            {lang === "en" ? (
              <>Send <strong className="text-white">{formatPrice(config.listing_fee)}</strong> via MTN Mobile Money to this number (<strong className="text-white">{config.momo_number}</strong>){config.momo_name ? <> <strong className="text-white">{config.momo_name}</strong></> : null}, using the reference. Then confirm below — our team verifies the payment and publishes your listing.</>
            ) : (
              <>Envoyez <strong className="text-white">{formatPrice(config.listing_fee)}</strong> via MTN Mobile Money à ce numéro (<strong className="text-white">{config.momo_number}</strong>){config.momo_name ? <> <strong className="text-white">{config.momo_name}</strong></> : null}, avec la référence. Confirmez ensuite — notre équipe vérifie le paiement et publie votre annonce.</>
            )}
          </div>

          <div className="mt-6">
            <label className="block text-white/70 text-sm mb-2">{t("pay_phone")}</label>
            <input data-testid="input-pay-phone" value={payPhone} onChange={(e) => setPayPhone(e.target.value)} className="w-full bg-black/40 border border-white/15 px-4 py-3 text-white outline-none focus:border-gold transition-colors placeholder-white/30" placeholder="+237 6XX XX XX XX" />
          </div>

          <button
            data-testid="confirm-payment-btn"
            disabled={submitting || !payPhone}
            onClick={confirmPayment}
            className="w-full mt-6 bg-gold text-black py-3.5 font-medium hover:bg-gold-light disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? t("paying") : (lang === "en" ? "I have paid — Submit for review" : "J'ai payé — Soumettre")}
          </button>
        </div>
      ) : (
        <div className="bg-[#121212] border border-white/10 p-10 text-center" data-testid="payment-success">
          <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mx-auto mb-5">
            <Check size={30} className="text-green-400" />
          </div>
          <h2 className="font-display text-2xl text-white mb-2">{lang === "en" ? "Submitted for review" : "Soumis pour révision"}</h2>
          {payCode && (
            <div className="max-w-sm mx-auto my-6 bg-black/40 border border-gold/30 p-5" data-testid="confirmation-code">
              <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{t("confirmation_code")}</p>
              <p className="font-display text-gold text-4xl tracking-[0.3em]">{payCode}</p>
              <p className="text-white/50 text-xs mt-3 leading-relaxed">{t("code_note")}</p>
            </div>
          )}
          <p className="text-white/50 max-w-md mx-auto mb-8">
            {lang === "en"
              ? "Thanks! Once we confirm your MoMo payment, your listing goes live for 30 days. Track its status in your dashboard."
              : "Merci ! Dès confirmation de votre paiement MoMo, votre annonce sera publiée pour 30 jours. Suivez son statut dans votre tableau de bord."}
          </p>
          <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard-btn" className="bg-gold text-black px-7 py-3 font-medium hover:bg-gold-light transition-colors">{t("nav_dashboard")}</button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, highlight, copy }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/50 text-sm">{label}</span>
      <span className={`flex items-center gap-2 ${highlight ? "text-gold font-display text-lg" : "text-white"}`}>
        {value}
        {copy && <button onClick={() => navigator.clipboard.writeText(value)} className="text-white/40 hover:text-gold"><Copy size={14} /></button>}
      </span>
    </div>
  );
}
